import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { Col, Divider, Form, Row } from 'antd';
import { DatePicker, Input, RichArea, Button, BUTTON_TYPES, RichText, User, AtsSkeleton } from '_atoms';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { getJobOfferRecommendation } from 'store/selectors/jobSelectors';
import { ReactComponent as Arrow } from 'dist/icons/Vector1.svg';
import type { OfferingCandidate } from 'store/reducers/jobReducer';
import type { UserRole } from '_atoms/User';
import { RULES } from './constants';
import './style.scss';

interface OfferProps {
  className?: string;
  defaultOpen?: boolean;
  offer?: OfferingCandidate;
  editable?: boolean;
  loading?: boolean;
  onSubmit: (data: Record<string, unknown>) => void;
}

interface OfferRecommendationData {
  approver?: { avatar?: string; full_name: string; roles?: UserRole[] };
  probation_period?: number | string;
  level?: string;
  comment?: string;
}

const Recommendation: React.FC = () => {
  const data = useSelector(getJobOfferRecommendation) as OfferRecommendationData | null;
  const { t } = useTranslation();

  if (!data?.approver) return null;
  return (
    <div className="job-offer__recommendation">
      <Typography.Title4 weight={TYPOGRAPHY_WEIGHT.BOLD}>
        {t('translation:offer.title_recommendation')}
      </Typography.Title4>
      <User avatar={data.approver.avatar} fullName={data.approver.full_name} roles={data.approver.roles} />
      <Typography.Label>{t('translation:offer.label_recommended_probation_period')}</Typography.Label>
      <Typography.Text>
        {data.probation_period} {t('translation:offer.text_months')}
      </Typography.Text>
      <Typography.Label>{t('translation:offer.label_assumed_experience_level')}</Typography.Label>
      <Typography.Text>{data.level}</Typography.Text>
      <Typography.Label>{t('translation:offer.label_comment')}</Typography.Label>
      <Typography.Text>{data.comment}</Typography.Text>
    </div>
  );
};

export const Offer: React.FC<OfferProps> = ({ className, defaultOpen, offer = {}, editable, loading, onSubmit }) => {
  const [ediMode, setEditMode] = useState(false);
  const [openDetails, setOpenDetails] = useState(defaultOpen);
  const { t } = useTranslation();

  const onOpenDetails = () => setOpenDetails(o => !o);
  const onEdit = () => setEditMode(true);
  const onCancelEdit = () => setEditMode(false);

  const onSubmitOffer = ({ date, probation_period, ...values }) => {
    onSubmit({
      ...values,
      expected_answer_date: date.format('YYYY-MM-DD'),
      probation_period: +probation_period,
      is_edit: ediMode,
    });
  };

  const isEmpty = !Object.keys(offer).length;
  const expected_answer_date = offer.expected_answer_date
    ? moment(offer.expected_answer_date, 'YYYY-MM-DD')
    : moment().add(1, 'week');
  const initialValues = {
    probation_period: offer.probation_period || '1',
    date: expected_answer_date,
    comment: offer.comment || '',
  };

  if (loading) {
    return (
      <div className={cn('job-offer', className)}>
        <div className="job-offer__header">
          <Typography.Title2 weight={TYPOGRAPHY_WEIGHT.BOLD}>{t('translation:offer.title')}</Typography.Title2>
        </div>
        <AtsSkeleton height={84} />
        <Divider />
      </div>
    );
  }

  if (isEmpty && !editable) return null;

  if (isEmpty || ediMode) {
    return (
      <div className={cn('job-offer', className, { open: openDetails })}>
        <div className="job-offer__header">
          <Typography.Title2 weight={TYPOGRAPHY_WEIGHT.BOLD}>{t('translation:offer.title')}</Typography.Title2>
          <Arrow className="open-details" onClick={onOpenDetails} />
        </div>
        <div className="job-offer__content">
          <Recommendation />
          <Form onFinish={onSubmitOffer} initialValues={initialValues} validateTrigger="onSubmit">
            <Row gutter={12}>
              <Col lg={12} md={12} sm={12}>
                <Input
                  label={t('translation:offer.label_probation_period')}
                  placeholder={t('translation:offer.ph_probation_period' as never)}
                  itemProps={{ name: 'probation_period', rules: RULES.PERIOD }}
                />
              </Col>
              <Col lg={12} md={12} sm={12}>
                <Typography.Text className="months">{t('translation:offer.text_months')}</Typography.Text>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col lg={12} md={12} sm={12}>
                <DatePicker
                  label={t('translation:offer.label_expected_answer_date')}
                  itemProps={{ name: 'date', rules: RULES.DATE }}
                />
              </Col>
            </Row>
            <Form.Item name="comment">
              <RichArea
                label={t('translation:offer.label_comment')}
                placeholder={t('translation:offer.ph_comment')}
                initialValue={offer.comment}
                maxLength={6000}
              />
            </Form.Item>
            <div className="job-offer__content-footer">
              {ediMode && <Button onClick={onCancelEdit}>{t('translation:offer.button_cancel')}</Button>}
              <Button htmlType="submit" type={BUTTON_TYPES.PRIMARY}>
                {ediMode ? t('translation:offer.button_save_changes') : t('translation:offer.button_add')}
              </Button>
            </div>
          </Form>
        </div>
        <Divider />
      </div>
    );
  }

  return (
    <div className={cn('job-offer', className, { open: openDetails })}>
      <div className="job-offer__header">
        <Typography.Title2 weight={TYPOGRAPHY_WEIGHT.BOLD}>{t('translation:offer.title')}</Typography.Title2>
        <Arrow className="open-details" onClick={onOpenDetails} />
      </div>
      <div className="job-offer__content">
        <Recommendation />
        {editable && (
          <Button onClick={onEdit} className="edit-button">
            {t('translation:offer.button_edit')}
          </Button>
        )}
        <div className="info-block">
          <Typography.Label>{t('translation:offer.label_probation_period')}</Typography.Label>
          <Typography.Text>{`${offer.probation_period} ${t('translation:offer.text_months')}`}</Typography.Text>
        </div>
        <div className="info-block">
          <Typography.Label>{t('translation:offer.label_expected_answer_date')}</Typography.Label>
          <Typography.Text>{expected_answer_date?.format('MMM DD, YYYY')}</Typography.Text>
        </div>
        <div className="info-block">
          <Typography.Label>{t('translation:offer.label_comment')}</Typography.Label>
          <RichText text={offer.comment || '-'} />
        </div>
      </div>
      <Divider />
    </div>
  );
};
